import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import * as Api from '@ViewModels';
import { type IContact, type IPhoneCall } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { queryKeys } from './queryKeys';

/**
 * Row shape returned by POST /phonecall/filter. Each entry wraps the
 * underlying phone call with company + contact references resolved server-side
 * so the row UI doesn't have to look them up.
 */
export type PhoneCallView = {
	phoneCall: IPhoneCall;
	company?: { id?: string; companyName?: string };
	contacts?: IContact[];
};

/**
 * Past phone calls for a single lead, filtered to those with a transcription.
 *
 * Backend contract (POST /phonecall/filter, criteria-based):
 *   - `User`            (required — data store rejects without it; we scope
 *                        to the current user)
 *   - `Company`         (added by Levitate-API PR #11316)
 *   - `HasTranscription` ("true")
 *
 * Returned array is sorted newest-first by `startTime` (falling back to
 * `creationDate` when `startTime` is absent — older records sometimes are).
 *
 * Empty array when the lead has no past transcribed calls for this user, or
 * when `companyId`/`userId` is undefined.
 */
export function usePhoneCallsByCompany(companyId: string | undefined): UseQueryResult<PhoneCallView[], Error> {
	const userSession = useUserSession();
	const userId = userSession.user?.id;

	return useQuery<PhoneCallView[], Error>({
		queryKey: queryKeys.focusedView.phoneCallsByCompany(companyId),
		enabled: !!companyId && !!userId,
		queryFn: async () => {
			const criteria: Array<{ property: string; value: string }> = [
				{ property: 'User', value: userId as string },
				{ property: 'Company', value: companyId as string },
				{ property: 'HasTranscription', value: 'true' },
			];
			const result = await userSession.webServiceHelper.callAsync<Api.IPagedCollection<PhoneCallView>>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: 'phonecall/filter',
					queryParams: { pageSize: '50' },
				}),
				'POST',
				{ criteria }
			);
			const values = result?.values ?? [];
			const tsOf = (v: PhoneCallView): number => {
				const ts = v.phoneCall?.startTime ?? v.phoneCall?.creationDate;
				return ts ? Date.parse(ts) : 0;
			};
			return [...values].sort((a, b) => tsOf(b) - tsOf(a));
		},
		retry: false,
		staleTime: 60 * 1000,
	});
}
